import { isSameLocalDay } from '@/lib/dateKey';
import type { Task, CalendarEvent } from '@/types';

export interface BusyInterval {
  startMs: number;
  endMs: number;
}

/**
 * Collect the busy windows on `date` from scheduled tasks (excluding done /
 * skipped) and calendar events — the input findFirstAvailableSlot searches
 * around. Previously duplicated in useCapture and SchedulePopover.
 */
export function toBusyIntervals(
  tasks: Task[],
  events: CalendarEvent[],
  date: Date,
): BusyInterval[] {
  const busy: BusyInterval[] = [];
  for (const t of tasks) {
    if (t.status === 'done' || t.status === 'skipped') continue;
    if (t.scheduledStart && t.scheduledEnd && isSameLocalDay(t.scheduledStart, date)) {
      busy.push({ startMs: t.scheduledStart.getTime(), endMs: t.scheduledEnd.getTime() });
    }
  }
  for (const e of events) {
    if (isSameLocalDay(e.start, date)) {
      busy.push({ startMs: e.start.getTime(), endMs: e.end.getTime() });
    }
  }
  return busy;
}

const DAY_START_HOUR = 7;
const DAY_END_HOUR = 22;
const SLOT_GRANULARITY_MIN = 15;

function setHours(date: Date, hour: number, minute = 0): Date {
  const d = new Date(date);
  d.setHours(hour, minute, 0, 0);
  return d;
}

function snapUpToGranularity(date: Date): Date {
  const d = new Date(date);
  const remainder = d.getMinutes() % SLOT_GRANULARITY_MIN;
  if (remainder !== 0) {
    d.setMinutes(d.getMinutes() + (SLOT_GRANULARITY_MIN - remainder), 0, 0);
  } else {
    d.setSeconds(0, 0);
  }
  return d;
}

/**
 * Find the first contiguous gap of `durationMinutes` within `[max(now, DAY_START_HOUR),
 * DAY_END_HOUR]` on `date` that doesn't overlap any busy interval. Returns the start
 * Date of that slot, or null if no slot fits.
 *
 * If `date` is in the future relative to `now`, `now` is ignored — start at DAY_START_HOUR.
 */
export function findFirstAvailableSlot(
  date: Date,
  durationMinutes: number,
  busy: BusyInterval[],
  now: Date = new Date(),
): Date | null {
  const windowStart = setHours(date, DAY_START_HOUR);
  const windowEnd = setHours(date, DAY_END_HOUR);

  let candidate = windowStart;
  if (isSameLocalDay(date, now) && now > windowStart) {
    candidate = snapUpToGranularity(now);
  }

  if (candidate >= windowEnd) return null;

  const sorted = [...busy].sort((a, b) => a.startMs - b.startMs);
  const durMs = durationMinutes * 60_000;

  while (candidate.getTime() + durMs <= windowEnd.getTime()) {
    const candStart = candidate.getTime();
    const candEnd = candStart + durMs;
    const overlap = sorted.find((b) => b.startMs < candEnd && b.endMs > candStart);
    if (!overlap) return candidate;
    candidate = snapUpToGranularity(new Date(overlap.endMs));
  }
  return null;
}
